import createDOMPurify, { type DOMPurify, type RemovedAttribute, type RemovedElement } from "dompurify";

/**
 * Event maps are uploaded per event and served from outside the application, so their
 * markup has to be treated as hostile input: an SVG can carry inline event handlers
 * (`onerror`, `onload`, ...), `<script>`, or HTML smuggled through `<foreignObject>`,
 * and all of those execute as soon as the markup is inserted into the document.
 */

/**
 * Elements the SVG profile allows but that a map never needs.
 *
 * `a` and the animation elements are the usual way of smuggling a script URL into an
 * otherwise harmless looking map (e.g. `<animate attributeName="href" values="javascript:...">`).
 */
const FORBIDDEN_TAGS = [
  "a",
  "animate",
  "animatecolor",
  "animatemotion",
  "animatetransform",
  "foreignobject",
  "iframe",
  "mpath",
  "script",
  "set",
  "use",
];

/** Attributes that carry a reference we validate ourselves. */
const REFERENCE_ATTRIBUTES = ["href", "xlink:href", "src"];

const DATA_IMAGE = /^data:image\/(?:png|jpeg|jpg|gif|webp)[;,]/i;
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const URI_WHITESPACE = /\s+/g;

let purifier: DOMPurify | null = null;

/** Attributes dropped by our own hook, which DOMPurify doesn't account for. */
let blocked = 0;

/**
 * Only same document fragments, relative paths, http(s) and raster data URIs are accepted.
 * `data:image/svg+xml` is rejected on purpose: it is a nested document, not a picture.
 */
function isSafeReference(value: string): boolean {
  // Browsers ignore whitespace and null bytes inside URIs, so `java\0script:` and
  // `java\nscript:` reach the same handler as the plain scheme.
  const uri = value.replace(URI_WHITESPACE, "").split("\u0000").join("");

  if (uri.startsWith("#")) return true;

  const scheme = SCHEME.exec(uri);

  if (scheme === null) return true;

  const protocol = scheme[1].toLowerCase();

  if (protocol === "http" || protocol === "https") return true;
  if (protocol === "data") return DATA_IMAGE.test(uri);

  return false;
}

function getPurifier(): DOMPurify {
  if (purifier !== null) return purifier;

  purifier = createDOMPurify(window);

  purifier.addHook("afterSanitizeAttributes", (node) => {
    for (const attribute of REFERENCE_ATTRIBUTES) {
      const value = node.getAttribute(attribute);

      if (value === null || isSafeReference(value)) continue;

      node.removeAttribute(attribute);
      blocked++;
    }

    // Belt and braces: the allow list already drops event handlers, but one of them
    // slipping through is the whole attack, so they get checked a second time.
    for (const { name } of [ ...node.attributes ]) {
      if (!name.toLowerCase().startsWith("on")) continue;

      node.removeAttribute(name);
      blocked++;
    }
  });

  return purifier;
}

/**
 * DOMPurify parses the markup inside an HTML document, so the `<body>` wrapper it strips
 * on the way out is noise, not something the map tried to sneak in.
 */
function isWrapper(entry: RemovedElement | RemovedAttribute): boolean {
  return "element" in entry && entry.element.nodeName === "BODY";
}

export interface SanitizedSvg {
  /** The sanitized root element, or `null` when the markup holds no usable SVG. */
  element: SVGSVGElement | null;
  /** How many nodes and attributes had to be stripped. */
  removed: number;
}

/**
 * Parses untrusted SVG markup and returns a root element that is safe to insert in the
 * document. Parsing happens in a detached document, so nothing loads or runs on the way in.
 */
export function sanitizeSvg(markup: string): SanitizedSvg {
  const purify = getPurifier();

  blocked = 0;

  const fragment = purify.sanitize(markup, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: FORBIDDEN_TAGS,
    ADD_ATTR: [ "xlink:href" ],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });

  return {
    element: fragment.querySelector("svg"),
    removed: purify.removed.filter(entry => !isWrapper(entry)).length + blocked,
  };
}
